import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      title: "Kaddee was patient",
      content: "Kaddee was patient, knowledgeable, & helpful. Not all of us are tech savvy.",
      author: "Wayne Drake",
      date: "4 days ago",
      verified: true
    },
    {
      id: 2,
      title: "Lisa Dunbar was absolutely amaz...",
      content: "Lisa Dunbar was absolutely amazing. The knowledge, wisdom, and",
      author: "Vanessa Lake",
      date: "4 days ago",
      verified: false
    },
    {
      id: 3,
      title: "Excellent",
      content: "Excellent",
      author: "Zelda Madame",
      date: "September 13",
      verified: true
    },
    {
      id: 4,
      title: "Everyone on your team has been...",
      content: "Everyone on your team has been very helpful.",
      author: "Kerry Ann Nagel",
      date: "September 11",
      verified: true
    }
  ];

  // Detectar si es móvil
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Efecto de carrusel automático (solo en desktop)
  useEffect(() => {
    if (!isHovered && !isMobile) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
        );
      }, 3000); // Cambia cada 3 segundos

      return () => clearInterval(interval);
    }
  }, [isHovered, testimonials.length, isMobile]);

  // Duplicar testimonios para crear un bucle infinito
  const extendedTestimonials = [...testimonials, ...testimonials];

  // Funciones de navegación
  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  return (
    <div className="bg-white py-8 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 md:mb-12">
          
          {/* Header izquierdo - Rating general */}
          <div className="mb-6 lg:mb-0 w-full lg:w-auto">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800">Great</h2>
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 fill-current" />
                ))}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <p className="text-gray-600 text-base sm:text-lg">Based on 1,668 reviews</p>
              <div className="flex items-center space-x-2 bg-green-100 px-3 py-1 rounded-full w-fit">
                <Star className="w-4 h-4 text-green-600 fill-current" />
                <span className="text-green-800 font-semibold text-sm">Trustpilot</span>
              </div>
            </div>
          </div>

          {/* Carrusel de testimonios */}
          <div className="flex-1 lg:ml-12 w-full">
            <div className="relative">
              {/* Testimonios - Desktop */}
              <div 
                className="hidden md:flex space-x-6 overflow-hidden pb-4"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div 
                  className="flex space-x-6 transition-transform duration-500 ease-in-out"
                  style={{ 
                    transform: `translateX(-${currentIndex * (320 + 24)}px)` // 320px width + 24px gap
                  }}
                >
                  {extendedTestimonials.map((testimonial, index) => (
                  <div key={`${testimonial.id}-${index}`} className="flex-shrink-0 w-80 bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    {/* Estrellas y verificación */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-green-500 fill-current" />
                        ))}
                      </div>
                      {testimonial.verified && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle size={16} />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                      )}
                    </div>

                    {/* Título */}
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                      {testimonial.title}
                    </h4>

                    {/* Contenido */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {testimonial.content}
                    </p>

                    {/* Autor y fecha */}
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">{testimonial.author}</span>
                      <span className="mx-1">•</span>
                      <span>{testimonial.date}</span>
                    </div>
                  </div>
                ))}
                </div>
              </div>

              {/* Testimonios - Mobile con scroll horizontal */}
              <div 
                ref={scrollContainerRef}
                className="md:hidden flex space-x-4 overflow-x-auto pb-4 scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={testimonial.id} className="flex-shrink-0 w-72 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    {/* Estrellas y verificación */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-green-500 fill-current" />
                        ))}
                      </div>
                      {testimonial.verified && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle size={14} />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                      )}
                    </div>

                    {/* Título */}
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                      {testimonial.title}
                    </h4>

                    {/* Contenido */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {testimonial.content}
                    </p>

                    {/* Autor y fecha */}
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">{testimonial.author}</span>
                      <span className="mx-1">•</span>
                      <span>{testimonial.date}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Flechas de navegación - Solo Desktop */}
              <div className="hidden md:block">
                <button 
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
                  onClick={prevTestimonial}
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <button 
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
                  onClick={nextTestimonial}
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Texto inferior */}
            <p className="text-sm text-gray-500 mt-4 text-center md:text-left">
              Showing our 4 & 5 star reviews
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
